import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { formatNumero } from "../../utils/Utilitats";

const PAGE_SIZES = [10, 25, 50];

function idMaterial(m) {
  return m ? Number(m.idMaterials) : null;
}

function idTipusDe(item) {
  return item ? Number(item.idTipusDeMaterials ?? item.TipusDeMaterials_idTipusDeMaterials) : null;
}

function idCategoriaDe(item) {
  return item ? Number(item.idCategories ?? item.Categories_idCategories) : null;
}

function esActiu(v) {
  return v === true || v === 1 || v === "1";
}

function Seleccionarmaterials({
  onMaterialSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedMaterial,
  filterText,
  setFilterText,
  categoriaFilter,
  setCategoriaFilter,
  tipusFilter,
  setTipusFilter,
  mostrarActius,
  setMostrarActius,
  reloadToken,
  setMaterialsList,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tipusList, setTipusList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "NomGeneric", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Materials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            mostrarActius: mostrarActius ? 1 : 0,
          }),
        });
        const result = await response.json();
        setMaterials(result.success ? result.materials || [] : []);
      } catch (error) {
        console.error("Error carregant Materials:", error);
        setMaterials([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, [empresaSeleccionada, mostrarActius, reloadToken]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Categories.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setCategories(result.success ? result.categories || [] : []);
      } catch (error) {
        console.error("Error carregant Categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchTipus = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Tipusdematerials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            mostrarActius: 1,
          }),
        });
        const result = await response.json();
        setTipusList(result.success ? result.tipusDeMaterials || [] : []);
      } catch (error) {
        console.error("Error carregant Tipus materials:", error);
        setTipusList([]);
      }
    };
    fetchTipus();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const lowerFilter = (filterText || "").toLowerCase();
    const filtered = (materials || []).filter((item) => {
      const matchesCategory =
        !categoriaFilter || idCategoriaDe(item) === Number(categoriaFilter);
      const matchesTipus = !tipusFilter || idTipusDe(item) === Number(tipusFilter);
      const matchesFilter =
        (item.Categoria || "").toLowerCase().includes(lowerFilter) ||
        (item.Tipus || "").toLowerCase().includes(lowerFilter) ||
        (item.NomGeneric || "").toLowerCase().includes(lowerFilter) ||
        (item.Descripcio || "").toLowerCase().includes(lowerFilter);
      return matchesCategory && matchesTipus && matchesFilter;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (sortConfig.key === "EstocFisic") {
          const aValue = Number(a.EstocFisic) || 0;
          const bValue = Number(b.EstocFisic) || 0;
          if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }
        const aValue = (a[sortConfig.key] || "").toString().toLowerCase();
        const bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredMaterials(filtered);
    setMaterialsList?.(filtered);
    setPage(1);
  }, [filterText, materials, categoriaFilter, tipusFilter, sortConfig, setMaterialsList]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, material) => {
    e.stopPropagation();
    const id = idMaterial(material);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredMaterials.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredMaterials.slice(inici, inici + pageSize);
  const materialMenu = menuObert ? filteredMaterials.find((m) => idMaterial(m) === menuObert) : null;
  const tipusFiltrats = (tipusList || []).filter(
    (t) => !categoriaFilter || idCategoriaDe(t) === Number(categoriaFilter)
  );

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (material) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idMaterial(material) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, material)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom, descripció, tipus o categoria…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Categoria</span>
          <select
            className="dv2-select"
            value={categoriaFilter}
            onChange={(e) => {
              setCategoriaFilter(e.target.value);
              setTipusFilter("");
            }}
          >
            <option value="">Totes</option>
            {categories.map((cat) => (
              <option key={cat.idCategories} value={String(cat.idCategories)}>
                {cat.Categoria}
              </option>
            ))}
          </select>
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Tipus</span>
          <select
            className="dv2-select"
            value={tipusFilter}
            onChange={(e) => setTipusFilter(e.target.value)}
          >
            <option value="">Tots</option>
            {tipusFiltrats.map((t) => (
              <option key={idTipusDe(t)} value={String(idTipusDe(t))}>
                {t.Tipus}
              </option>
            ))}
          </select>
        </div>
        <label className="dv2-filter dv2-filter--toggle">
          <span className="dv2-filter-label">Mostrar actius</span>
          <span className="dv2-toggleRow">
            <span className="dv2-toggle">
              <input
                type="checkbox"
                checked={mostrarActius}
                onChange={() => setMostrarActius(!mostrarActius)}
              />
              <span className="dv2-toggle-track" />
            </span>
            {mostrarActius ? "Actius" : "Inactius"}
          </span>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant materials…</p>
      ) : filteredMaterials.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap material.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((material) => (
                <article
                  key={idMaterial(material)}
                  className="dv2-record"
                  onClick={() => onMaterialSelect?.(material)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(material)}
                >
                  <div>
                    <p className="dv2-record-title">{material.NomGeneric || "—"}</p>
                    <p className="dv2-record-meta">
                      {[material.Categoria, material.Tipus, esActiu(material.Actiu) ? "Actiu" : "Inactiu"]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="dv2-record-meta">Estoc {formatNumero(material.EstocFisic)}</p>
                  </div>
                  {renderAccions(material)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    <th
                      className={sortConfig.key === "Categoria" ? "is-sorted" : ""}
                      onClick={() => handleSort("Categoria")}
                    >
                      Categoria {sortConfig.key === "Categoria" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className={sortConfig.key === "Tipus" ? "is-sorted" : ""}
                      onClick={() => handleSort("Tipus")}
                    >
                      Tipus {sortConfig.key === "Tipus" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className={sortConfig.key === "NomGeneric" ? "is-sorted" : ""}
                      onClick={() => handleSort("NomGeneric")}
                    >
                      Nom genèric {sortConfig.key === "NomGeneric" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className={sortConfig.key === "Descripcio" ? "is-sorted" : ""}
                      onClick={() => handleSort("Descripcio")}
                    >
                      Descripció {sortConfig.key === "Descripcio" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className={`dv2-num ${sortConfig.key === "EstocFisic" ? "is-sorted" : ""}`}
                      onClick={() => handleSort("EstocFisic")}
                    >
                      Estoc físic{" "}
                      {sortConfig.key === "EstocFisic" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((material) => (
                    <tr
                      key={idMaterial(material)}
                      className={idMaterial(selectedMaterial) === idMaterial(material) ? "is-selected" : ""}
                      onClick={() => onMaterialSelect?.(material)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(material)}
                    >
                      <td>{material.Categoria || "—"}</td>
                      <td>{material.Tipus || "—"}</td>
                      <td>{material.NomGeneric || "—"}</td>
                      <td>{material.Descripcio || "—"}</td>
                      <td className="dv2-num">{formatNumero(material.EstocFisic)}</td>
                      <td className="dv2-table-actions">{renderAccions(material)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage(1)}
                aria-label="Primera pàgina"
              >
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Pàgina anterior"
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Pàgina següent"
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
                aria-label="Última pàgina"
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
      {materialMenu &&
        menuPos &&
        createPortal(
          <div
            className="dv2-menu dv2-menu--fixed"
            ref={menuRef}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {pucConsultar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onConsultar?.(materialMenu);
                }}
              >
                Consultar
              </button>
            )}
            {pucModificar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onModificar?.(materialMenu);
                }}
              >
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => {
                  setMenuObert(null);
                  onEsborrar?.(materialMenu);
                }}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionarmaterials;
